// formatPostTime.ts — post-timestamp display, driven by the Language & Region
// prefs. Mirrors mobile core/lib/formatPostTimestamp.ts: hand-rolled (no
// Intl.DateTimeFormat) so web and mobile render byte-identical strings, in the
// device's LOCAL timezone.
//
//   relative (default) → the server's compact string ("2h") untouched
//   absolute           → "2:42 PM · Jun 12, 2026"  (12h)
//                        "14:42 · Jun 12, 2026"    (24h)
//
// Falls back to the relative string whenever createdAt is missing or invalid —
// older cached posts and the two decorative seed variants stay renderable.

import type { TimestampStyleKey, ClockFormatKey } from '@/core/models/preferences';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * ISO instant → the absolute time as separate parts, [clock, date]
 * (["2:42 PM", "Jun 12, 2026"]), for a visual run laid out with MetaRow.
 * Empty for an invalid instant.
 */
export function formatAbsoluteParts(iso: string, clock: ClockFormatKey): string[] {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return [];
  const hours24 = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, '0');
  const clockPart =
    clock === '24h'
      ? `${String(hours24).padStart(2, '0')}:${mm}`
      : `${hours24 % 12 === 0 ? 12 : hours24 % 12}:${mm} ${hours24 >= 12 ? 'PM' : 'AM'}`;
  return [clockPart, `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`];
}

/** ISO instant → absolute local string, honouring the clock-format pref. */
export function formatAbsolute(iso: string, clock: ClockFormatKey): string {
  return formatAbsoluteParts(iso, clock).join(', ');
}

/** The one entry point post cards use — style pref decides, with safe fallback. */
export function formatPostTime(
  relative: string,
  createdAt: string | undefined,
  style: TimestampStyleKey,
  clock: ClockFormatKey,
): string {
  if (style !== 'absolute' || !createdAt) return relative;
  return formatAbsolute(createdAt, clock) || relative;
}

/** formatPostTime as separate parts for a MetaRow: [relative] or [clock, date]. */
export function formatPostTimeParts(
  relative: string,
  createdAt: string | undefined,
  style: TimestampStyleKey,
  clock: ClockFormatKey,
): string[] {
  if (style !== 'absolute' || !createdAt) return [relative];
  const parts = formatAbsoluteParts(createdAt, clock);
  return parts.length > 0 ? parts : [relative];
}

/**
 * ISO instant → the server's compact relative string ("now" | "2m" | "3h" | "5d" | "2w"),
 * mirroring apps/content/serializers.py relative_time() exactly. Used to render
 * edit-history timestamps, which are stored as real ISO instants (not frozen
 * relative strings — see PostViewSet.partial_update) and are formatted relative
 * to NOW at render time, so "2h" keeps advancing correctly on every render.
 *
 * Backward compat: edit-history rows written before this change hold a legacy
 * relative string directly (e.g. "now", "2h") rather than an ISO instant. Those
 * are not valid dates, so this returns the input unchanged for them.
 */
export function formatRelativeFromIso(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 60) return 'now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}
